import { useMemo, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Printer, TriangleAlert } from 'lucide-react';
import { colors } from '@/lib/ui-tokens';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field, Select } from '@/components/ui/field';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { QrCode } from './components/QrCode';

interface TableRow {
  id: string;
  number: string;
  name: string | null;
  seats: number;
  qrToken: string | null;
  area?: { id: string; name: string; floorId: string } | null;
}

interface AreaRow {
  id: string;
  name: string;
  floorId: string;
}

const ORIGIN_KEY = 'venue.guestOrigin';

/**
 * The customer-web origin is a deployment detail, not a tenant setting, so it
 * comes from the build env (`VITE_CUSTOMER_WEB_URL`) and stays editable here for
 * venues whose guest app lives on their own domain.
 *
 * D-10 — there is deliberately NO fallback. This used to guess
 * `location.origin.replace(':5273', ':5275')`, which is a dev-port trick and a
 * no-op anywhere else: in production every code encoded the dashboard's own
 * address, so a guest scanning a table tent landed on the staff login. A wrong
 * QR code is only discovered after a hundred cards have been printed and glued
 * to tables, so an unset origin has to stop the print, not be papered over.
 */
function defaultGuestOrigin(): string {
  const stored = typeof localStorage === 'undefined' ? null : localStorage.getItem(ORIGIN_KEY);
  if (stored) return stored.trim();
  return (import.meta.env.VITE_CUSTOMER_WEB_URL ?? '').trim().replace(/\/$/, '');
}

/** Why this origin cannot be printed, or `null` when it can. */
type OriginProblem = 'missing' | 'malformed' | 'isDashboard';

function originProblem(value: string): OriginProblem | null {
  const trimmed = value.trim();
  if (!trimmed) return 'missing';
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return 'malformed';
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return 'malformed';
  // The failure mode D-10 is about: the dashboard's own origin looks perfectly
  // valid and sends every guest to a login screen.
  if (typeof location !== 'undefined' && parsed.origin === location.origin) return 'isDashboard';
  return null;
}

/** P2-07 — one printable table tent per table, QR generated in the browser. */
export default function TableQrSheet({ onBack }: { onBack?: () => void }) {
  const { t } = useTranslation();
  const [areaId, setAreaId] = useState('');
  const [columns, setColumns] = useState(3);
  const [showUrl, setShowUrl] = useState(true);
  const [origin, setOrigin] = useState(defaultGuestOrigin);
  const problem = originProblem(origin);

  const areas = useApi<unknown>('/areas', { limit: 200 });
  const tables = useApi<unknown>('/tables', {
    limit: 200,
    sortBy: 'number',
    sortOrder: 'asc',
    ...(areaId ? { areaId } : {}),
  });

  const areaRows = rowsOf<AreaRow>(areas.data).rows;
  const tableRows = rowsOf<TableRow>(tables.data).rows;

  const guestUrl = useMemo(
    () => (token: string) => `${origin.replace(/\/$/, '')}/scan?t=${token}`,
    [origin],
  );

  const missingToken = tableRows.filter((table) => !table.qrToken).length;
  /**
   * A card is only printable when the table has a token AND the origin is
   * usable — those are exactly the two conditions the card renderer below uses
   * to decide whether to draw a code at all. With none of them printable the
   * sheet is a page of blanks, so "Print" would only waste paper.
   */
  const printable = problem ? 0 : tableRows.length - missingToken;
  const printBlocked = problem
    ? t(`spaces.qr.origin.${problem}`)
    : printable === 0
      ? t('spaces.qr.nothingToPrint')
      : null;

  return (
    <div className="space-y-4">
      <style>{`
        .qr-sheet-grid {
          display: grid;
          grid-template-columns: repeat(var(--qr-cols), minmax(0, 1fr));
          gap: 12px;
        }
        .qr-card {
          border: 1px dashed ${colors.neutral[300]};
          border-radius: 12px;
          padding: 16px;
          text-align: center;
          /* Paper white: a QR needs maximum contrast when printed. */
          background: #fff;
          color: ${colors.neutral[900]};
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
        }
        .qr-card-number { font-size: 26px; font-weight: 700; line-height: 1.1; }
        .qr-card-area { font-size: 13px; color: ${colors.neutral[500]}; }
        .qr-card-cta { font-size: 13px; font-weight: 600; }
        .qr-card-url { font-size: 9px; color: ${colors.neutral[500]}; word-break: break-all; }
        .qr-card-seats { font-size: 11px; color: ${colors.neutral[500]}; }

        @page { size: A4 portrait; margin: 10mm; }

        @media print {
          body * { visibility: hidden !important; }
          .qr-sheet, .qr-sheet * { visibility: visible !important; }
          .qr-sheet {
            position: absolute;
            inset-block-start: 0;
            inset-inline-start: 0;
            width: 100%;
          }
          .qr-sheet-grid { gap: 6mm; }
          .qr-card { break-inside: avoid; page-break-inside: avoid; padding: 6mm; }
        }
      `}</style>

      <div className="qr-sheet-chrome flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-center gap-2">
          {onBack && (
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="h-4 w-4" />
              {t('common.back')}
            </Button>
          )}
          <div>
            <h1 className="text-2xl font-semibold">{t('spaces.qr.title')}</h1>
            <p className="text-sm text-ink-muted">{t('spaces.qr.subtitle')}</p>
          </div>
        </div>
        <Button
          onClick={() => window.print()}
          disabled={printBlocked !== null}
          title={printBlocked ?? undefined}
        >
          <Printer className="h-4 w-4" />
          {t('spaces.qr.print')}
        </Button>
      </div>

      <Card className="qr-sheet-chrome">
        <CardContent className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label={t('spaces.qr.area')}>
            <Select value={areaId} onChange={(event) => setAreaId(event.currentTarget.value)}>
              <option value="">{t('spaces.qr.allAreas')}</option>
              {areaRows.map((area) => (
                <option key={area.id} value={area.id}>
                  {area.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={t('spaces.qr.perRow')}>
            <Select
              value={columns}
              onChange={(event) => setColumns(Number(event.currentTarget.value))}
            >
              {[2, 3, 4].map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Select>
          </Field>

          <Field
            label={t('spaces.qr.guestUrl')}
            hint={t('spaces.qr.guestUrlHint')}
            error={problem ? t(`spaces.qr.origin.${problem}`) : undefined}
            required
          >
            <Input
              value={origin}
              placeholder="https://order.example.com"
              onChange={(event) => {
                setOrigin(event.currentTarget.value);
                localStorage.setItem(ORIGIN_KEY, event.currentTarget.value);
              }}
              inputMode="url"
            />
          </Field>

          <Field label={t('spaces.qr.options')}>
            <label className="flex h-11 items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={showUrl}
                onChange={(event) => setShowUrl(event.currentTarget.checked)}
              />
              {t('spaces.qr.showUrl')}
            </label>
          </Field>
        </CardContent>
      </Card>

      {problem && (
        <p className="qr-sheet-chrome flex items-start gap-2 rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/50 p-3 text-sm text-red-700 dark:text-red-400">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <strong className="font-semibold">{t('spaces.qr.origin.blocked')}</strong>{' '}
            {t(`spaces.qr.origin.${problem}`)}
          </span>
        </p>
      )}

      {missingToken > 0 && (
        <p className="qr-sheet-chrome flex items-center gap-2 text-sm text-amber-600">
          <TriangleAlert className="h-4 w-4" />
          {t('spaces.qr.missingToken', { count: missingToken })}
        </p>
      )}

      {tables.loading ? (
        <LoadingState label={t('common.loading')} />
      ) : tables.error ? (
        <ErrorState message={tables.error} onRetry={tables.reload} />
      ) : tableRows.length === 0 ? (
        <EmptyState title={t('spaces.qr.noTables')} hint={t('spaces.qr.noTablesHint')} />
      ) : (
        <div className="qr-sheet">
          <div
            className="qr-sheet-grid"
            style={{ '--qr-cols': columns } as CSSProperties}
          >
            {tableRows.map((table) => {
              // No origin, no code: a card is either right or blank, never a guess.
              const url = !problem && table.qrToken ? guestUrl(table.qrToken) : null;
              return (
                <article key={table.id} className="qr-card">
                  <p className="qr-card-number">
                    {t('spaces.qr.tableNumber', { number: table.number })}
                  </p>
                  <p className="qr-card-area">{table.area?.name ?? ''}</p>
                  {url ? (
                    <QrCode value={url} size={168} title={url} />
                  ) : (
                    <p className="qr-card-url">
                      {problem ? t('spaces.qr.origin.noCode') : t('spaces.qr.noToken')}
                    </p>
                  )}
                  <p className="qr-card-cta">{t('spaces.qr.scanToOrder')}</p>
                  <p className="qr-card-seats">{t('table.seats', { count: table.seats })}</p>
                  {showUrl && url && <p className="qr-card-url">{url}</p>}
                </article>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
